// src/components/HomePage/ProofSection.jsx
import React, { memo, useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/*
// Старые видео (закомментированы по запросу):
const OLD_DEFAULT_VIDEOS = [
  {
    title: 'Аудит RCON и обход авторизации на Vanilla 1.20',
    duration: '12:45',
    views: '24.8K просмотров',
    link: 'https://youtube.com'
  },
  {
    title: 'Стресс-тестирование сетевого шлюза BungeeCord 50k pps',
    duration: '08:15',
    views: '18.2K просмотров',
    link: 'https://youtube.com'
  },
  {
    title: 'Демонстрация Zero-Day метода на защищенном хабе',
    duration: '05:42',
    views: '42.1K просмотров',
    link: 'https://youtube.com'
  },
  {
    title: 'Автоматический парсинг 500 серверов и дамп метаданных',
    duration: '14:02',
    views: '19.7K просмотров',
    link: 'https://youtube.com'
  }
];
*/

// Извлечение YouTube Video ID из ссылки любого формата
export const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : null;
};

// Получение ссылки на превью YouTube
export const getYouTubeThumbnail = (id, quality = 'maxresdefault') => {
  if (!id) return null;
  return `https://img.youtube.com/vi/${id}/${quality}.jpg`;
};

// Форматирование количества просмотров
export const formatViews = (views) => {
  if (!views) return '';
  const str = String(views).trim();
  if (str.toLowerCase().includes('просмотр') || str.toLowerCase().includes('view')) {
    return str;
  }
  return `${str} просмотров`;
};

// Базовая метаинформация для мгновенной отрисовки без задержек сети
const KNOWN_METADATA = {
  'oXiXIznffYc': {
    title: 'Защититься от этого способа взлома НЕВОЗМОЖНО. Как ИИ взламывает сервера майнкрафт',
    duration: '08:39',
    views: '3.2K'
  },
  '_sWRXkrM8p0': {
    title: 'Сайт который поставил под угрозу все снг сервера майнкрафт. Как взламывал, так и будет взламывать',
    duration: '07:21',
    views: '7.6K'
  },
  'AQnIZVUNEyU': {
    title: 'Способ остановки серверов майнкрафт от ИИ и краш Skybars | Методы взлома и краша серверов майнкрафт',
    duration: '06:21',
    views: '5.9K'
  },
  'zRSQlQqYZp8': {
    title: 'Чекер аккаунтов майнкрафт, который раньше был приватным, или как взломать аккаунт на сервере',
    duration: '03:40',
    views: '8.9K'
  }
};

// Идентификаторы старых видео, которые были закомментированы
const OLD_VIDEO_IDS = new Set([
  'SIOUfyAOnpo',
  'EKg_ZILZn_Q',
  '57dxzQV7CQU',
  'uK_1JiPjUcQ',
  'od_2z9-Fcyk',
  'CtvmDomXl2w'
]);

// Новые видео доказательств эффективности
const DEFAULT_VIDEOS = [
  {
    link: 'https://youtu.be/oXiXIznffYc?si=QXNcYXin2y4silsl'
  },
  {
    link: 'https://youtu.be/_sWRXkrM8p0?si=j2-EcxOM8mZMVozf'
  },
  {
    link: 'https://youtu.be/AQnIZVUNEyU?si=iU8qfBO-FM0y_zYq'
  },
  {
    link: 'https://youtu.be/zRSQlQqYZp8?si=Napji970JHPPyetU'
  }
];

const ProofSection = memo(({ proofs = [], isLoading, error, onVideoClick }) => {
  const { t } = useLanguage();
  const [liveMetadata, setLiveMetadata] = useState({});

  // Если с бэкенда пришли старые закомментированные видео (до перезапуска ядра), отфильтровываем их
  const validProofs = proofs && proofs.length > 0
    ? proofs.filter(p => !OLD_VIDEO_IDS.has(extractYouTubeId(p.link)))
    : [];

  const baseVideos = validProofs.length > 0 ? validProofs : DEFAULT_VIDEOS;

  // Автоматический клиентский запрос к YouTube (oEmbed) для получения актуальных названий и превью
  useEffect(() => {
    let isMounted = true;

    baseVideos.forEach((video) => {
      const vidId = extractYouTubeId(video.link);
      if (!vidId) return;

      const ytOembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${vidId}&format=json`;

      fetch(ytOembedUrl)
        .then((res) => {
          if (!res.ok) throw new Error('YouTube oEmbed not OK');
          return res.json();
        })
        .then((data) => {
          if (isMounted && data && data.title) {
            setLiveMetadata((prev) => ({
              ...prev,
              [vidId]: {
                title: data.title,
                thumbnail: data.thumbnail_url || getYouTubeThumbnail(vidId, 'maxresdefault'),
                author: data.author_name
              }
            }));
          }
        })
        .catch(() => {
          // Клиентский запасной источник, если прямой oembed YouTube недоступен
          const fallbackUrl = `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${vidId}`;
          fetch(fallbackUrl)
            .then((res) => res.json())
            .then((data) => {
              if (isMounted && data && data.title) {
                setLiveMetadata((prev) => ({
                  ...prev,
                  [vidId]: {
                    title: data.title,
                    thumbnail: data.thumbnail_url || getYouTubeThumbnail(vidId, 'maxresdefault')
                  }
                }));
              }
            })
            .catch(() => {
              // В случае оффлайна или блокировки останутся статические данные
            });
        });
    });

    return () => {
      isMounted = false;
    };
  }, [baseVideos]);

  const displayVideos = baseVideos.map((video, idx) => {
    const vidId = extractYouTubeId(video.link);
    const live = vidId ? liveMetadata[vidId] : null;
    const known = vidId ? KNOWN_METADATA[vidId] : null;

    const title = (live && live.title) || video.title || (known && known.title) || 'Видеозапись аудита';
    const duration = video.duration || (known && known.duration) || 'HD';
    const rawViews = video.views || (known && known.views) || '';
    const views = formatViews(rawViews);
    const thumbnail = (live && live.thumbnail) || video.thumbnail || (vidId ? getYouTubeThumbnail(vidId, 'maxresdefault') : null);

    return {
      ...video,
      id: vidId,
      title,
      duration,
      views,
      thumbnail,
      link: video.link || 'https://youtube.com'
    };
  });

  return (
    <section className="mb-12 sm:mb-20" id="proofsSection">
      {/* Section Header */}
      <div className="flex flex-col gap-1 pb-4 mb-5 sm:mb-6 border-b border-white/[0.15] px-1.5 sm:px-1">
        <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
          {t('proofOfEffectiveness') || 'Доказательства эффективности'}
        </h2>
        <span className="text-xs sm:text-base text-white/80 font-normal">
          Видеозаписи практических аудитов и нагрузочного тестирования
        </span>
      </div>

      {/* 4-Column Video Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-[18px]">
        {displayVideos.map((video, idx) => (
          <a
            key={video.id || idx}
            href={video.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (onVideoClick) {
                onVideoClick(video.title);
              }
            }}
            className="rounded-[16px] sm:rounded-[18px] overflow-hidden flex flex-col border border-white/[0.08] hover:border-white bg-[#111319] hover:bg-[#161922] transition-all duration-300 group shadow-[0_4px_16px_rgba(0,0,0,0.3)] hover:-translate-y-1 hover:shadow-[0_16px_36px_-8px_rgba(0,0,0,0.85),0_0_20px_rgba(255,255,255,0.08)]"
          >
            {/* Thumbnail */}
            <div className="relative w-full h-[180px] sm:h-[160px] bg-[#090a0f] flex items-center justify-center overflow-hidden">
              {video.thumbnail ? (
                <img
                  src={video.thumbnail}
                  alt={video.title || ''}
                  loading="lazy"
                  onError={(e) => {
                    if (video.id && !e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = 'true';
                      e.currentTarget.src = getYouTubeThumbnail(video.id, 'hqdefault');
                    }
                  }}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center relative"
                  style={{ background: 'linear-gradient(135deg, #161922 0%, #0a0b10 100%)' }}
                />
              )}

              {/* Play Badge Center */}
              <div className="absolute w-11 h-11 sm:w-[50px] sm:h-[50px] rounded-full bg-white text-black flex items-center justify-center shadow-[0_4px_18px_rgba(255,255,255,0.25)] group-hover:scale-[1.12] group-hover:bg-[#ececee] group-hover:shadow-[0_6px_26px_rgba(255,255,255,0.4)] transition-all duration-200 z-[2]">
                <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-[18px] sm:h-[18px] fill-black ml-0.5">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>

              {/* Duration Badge */}
              {video.duration && (
                <span className="absolute bottom-2 right-2 bg-black text-white text-[11px] font-bold px-2 py-0.5 rounded-md border border-white/30 z-[2] font-mono">
                  {video.duration}
                </span>
              )}
            </div>

            {/* Video Info */}
            <div className="p-3.5 sm:p-4 flex flex-col flex-1 justify-between gap-2.5 sm:gap-3">
              <h4 className="text-sm sm:text-[0.98rem] font-bold text-white leading-snug group-hover:text-white transition-colors line-clamp-2">
                {video.title}
              </h4>
              {video.views && (
                <div className="flex items-center text-xs text-white/60 font-medium">
                  <span>{video.views}</span>
                </div>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
});

export default ProofSection;